import { MarqueeManager } from './ui/MarqueeManager.js';

const animalNames = [
  'Lion', 'Tiger', 'Bear', 'Wolf', 'Fox', 'Rabbit', 'Deer', 'Eagle',
  'Hawk', 'Owl', 'Snake', 'Shark', 'Whale', 'Dolphin', 'Turtle', 'Frog',
  'Horse', 'Zebra', 'Giraffe', 'Elephant', 'Rhino', 'Hippo', 'Monkey', 'Gorilla',
  'Panda', 'Koala', 'Kangaroo', 'Penguin', 'Seal', 'Otter', 'Beaver', 'Squirrel',
  'Raccoon', 'Skunk', 'Badger', 'Hedgehog', 'Bat', 'Mouse', 'Rat', 'Cat',
  'Dog', 'Pig', 'Cow', 'Sheep', 'Goat', 'Chicken', 'Duck', 'Goose',
  'Peacock', 'Flamingo', 'Parrot', 'Raven', 'Crow', 'Sparrow', 'Robin', 'Cardinal'
];

export function generateAnimalName() {
  const animal = animalNames[Math.floor(Math.random() * animalNames.length)];
  const number = Math.floor(Math.random() * 9999) + 1;
  return `${animal}${number}`;
}

// Theme loading system
let currentTheme = null;
let selectedThemeId = 'soulwars-default';

export function setSelectedThemeId(themeId) {
  selectedThemeId = themeId;
}

export function getSelectedThemeId() {
  return selectedThemeId;
}

// Clear cached data when switching themes
export function clearThemeCache() {
  // Clear battlefields cache
  battlefieldsData = null;

  // Clear intermissions cache
  intermissionsCache = null;

  // Clear items cache
  itemsCache = null;

  // Clear music manifest cache
  musicManifestCache = null;

  // Clear SFX manifest cache
  sfxManifestCache = null;

  // Clear food data cache
  foodDataCache = null;

  console.log('🧹 Theme caches cleared');
}

// Refresh battlefield data from server
export async function refreshBattlefieldData() {
  try {
    // Clear the cache to force reload
    battlefieldsData = null;

    // Reload theme data to get fresh battlefield data
    await ensureThemeLoaded();

    // Reload battlefields data
    battlefieldsData = getThemeData('battlefields', []);

    console.log(`🔄 Refreshed battlefield data: ${battlefieldsData.length} battlefields`);
    return battlefieldsData;
  } catch (error) {
    console.error('Failed to refresh battlefield data:', error);
    return [];
  }
}

// Load new sprite assets that aren't already loaded
export async function loadNewBattlefieldSprites(scene, battlefields) {
  const assetsToLoad = [];
  const loadedKeys = new Set(Object.keys(scene.textures.list));

  // Get theme for S3 base path
  const theme = getCurrentTheme();
  const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
  let s3BasePath = theme?.s3BasePath || (theme?.id ? `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}` : '');
  
  // Remove trailing slash from s3BasePath to avoid double slashes
  if (s3BasePath && s3BasePath.endsWith('/')) {
    s3BasePath = s3BasePath.slice(0, -1);
  }
  
  const useS3 = !!s3BasePath;

  for (const battlefield of battlefields) {
    if (battlefield.image && !loadedKeys.has(battlefield.image)) {
      // Determine asset path based on theme
      let assetPath;
      if (useS3) {
        // Use S3 path
        const imagePath = battlefield.image.startsWith('battlefields/')
          ? battlefield.image
          : `battlefields/${battlefield.image}`;
        assetPath = `${s3BasePath}/${imagePath}`;
      } else {
        // Use local path
        assetPath = battlefield.image.startsWith('battlefields/')
          ? `assets/${battlefield.image}`
          : `assets/battlefields/${battlefield.image}`;
      }

      assetsToLoad.push({
        key: battlefield.image,
        path: assetPath
      });
    }
  }

  if (assetsToLoad.length > 0) {
    console.log(`🎨 Loading ${assetsToLoad.length} new battlefield sprites...`, { useS3, theme: theme?.id });

    // Load assets using a Promise-based approach
    return new Promise((resolve, reject) => {
      assetsToLoad.forEach(({ key, path }) => {
        console.log(`  📸 Queueing: ${key} → ${path}`);
        scene.load.image(key, path);
      });

      scene.load.once('complete', () => {
        console.log(`✅ Loaded ${assetsToLoad.length} new battlefield sprites`);
        resolve();
      });

      scene.load.once('filecomplete', (key) => {
        console.log(`  ✅ Loaded battlefield sprite: ${key}`);
      });

      scene.load.once('loaderror', (file) => {
        console.error(`❌ CORS/Load Error - Failed to load battlefield sprite:`, {
          key: file.key,
          url: file.url,
          src: file.src,
          theme: theme?.id,
          s3BasePath,
          useS3
        });
      });

      scene.load.start();
    });
  } else {
    console.log('✅ All battlefield sprites already loaded');
    return Promise.resolve();
  }
}

// Stop and clean up audio when switching themes
export function cleanupThemeAudio(scene) {
  if (!scene) return;

  try {
    // Stop battle music
    if (scene.battleMusic) {
      if (scene.battleMusic.isPlaying) {
        scene.battleMusic.stop();
      }
      scene.battleMusic.destroy();
      scene.battleMusic = null;
    }

    // Clear battle music queue and keys
    scene.battleMusicQueue = [];
    scene.battleMusicKeys = [];
    scene.lastBattleMusicKey = null;

    console.log('🔊 Theme audio cleaned up');
  } catch (error) {
    console.warn('Error during theme audio cleanup:', error);
  }
}

// Clear Phaser caches for theme assets
export function clearPhaserThemeCaches(scene) {
  if (!scene || !scene.cache) return;

  try {
    // Note: Phaser cache clearing is tricky as it can affect other scenes
    // We'll rely on theme-specific asset keys to avoid conflicts
    // Individual assets will be replaced when the new theme loads

    console.log('🎯 Phaser caches ready for theme switch');
  } catch (error) {
    console.warn('Error clearing Phaser caches:', error);
  }
}

// Reset game state managers for theme switch
export function resetThemeDependentManagers(scene) {
  if (!scene) return;

  try {
    // Reset IntermissionManager state
    if (scene.intermissionManager) {
      scene.intermissionManager.intermissionActive = false;
      scene.intermissionManager.intermissionLoading = false;
      scene.intermissionManager.currentMusic = null;
      scene.intermissionManager.battleMusicPrevVolume = null;
      console.log('🎭 IntermissionManager reset for theme switch');
    }

    // Reset SpellManager (spells may be theme-specific)
    if (scene.spellManager) {
      scene.spellManager.spells = {};
      scene.spellManager.spellSounds = [];
      console.log('✨ SpellManager reset for theme switch');
    }

    // Clear any active tweens that might reference old assets
    if (scene.tweens) {
      scene.tweens.killAll();
      console.log('🎬 Active tweens cleared');
    }

  } catch (error) {
    console.warn('Error resetting theme-dependent managers:', error);
  }
}

export async function loadTheme(themeId = null) {
  try {
    // If no themeId provided, use the selected theme ID
    if (!themeId) {
      themeId = selectedThemeId;
    }

    console.log(`🎨 Loading theme: ${themeId}`);

    const response = await fetch(`/api/themes/normalized/${themeId}`, {
      headers: {
        'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load theme ${themeId}: ${response.status}`);
    }

    const theme = await response.json();
    currentTheme = theme;

    console.log(`✅ Theme loaded: ${theme.name} (${theme.id})`);
    return theme;
  } catch (error) {
    console.error(`❌ Failed to load theme ${themeId}:`, error);

    // Try to load fallback theme if current theme failed
    if (themeId !== 'soulwars-default') {
      console.log('🔄 Attempting to load default SoulWars theme as fallback...');
      return loadTheme('soulwars-default');
    }

    throw error;
  }
}

export async function ensureThemeLoaded(themeId = null) {
  if (!currentTheme || (themeId && currentTheme.id !== themeId)) {
    await loadTheme(themeId);
  }
  return currentTheme;
}

export function getCurrentTheme() {
  return currentTheme;
}

// Fallback system - get data from theme or fallback to default values
export function getThemeData(path, fallback = null) {
  if (!currentTheme) {
    console.warn('⚠️  No theme loaded, using fallback for:', path);
    return fallback;
  }

  const keys = path.split('.');
  let value = currentTheme;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      console.warn(`⚠️  Theme data missing for path: ${path}, using fallback`);
      return fallback;
    }
  }

  return value;
}

// Battlefield utilities
let battlefieldsData = null;

export async function loadBattlefieldsData() {
  if (!battlefieldsData) {
    try {
      await ensureThemeLoaded();
      battlefieldsData = getThemeData('battlefields', []);
      // Normalize optional object_scale per battlefield (default 1.0 small, 2.0 large if provided)
      if (Array.isArray(battlefieldsData)) {
        battlefieldsData = battlefieldsData.map(bf => ({
          ...bf,
          object_scale: (typeof bf.object_scale === 'number') ? bf.object_scale : ((bf.size === 'large' || bf.size === 'largew') ? 2.0 : 1.0)
        }));
      }
    } catch (error) {
      console.error('Failed to load battlefields data:', error);
      battlefieldsData = [];
    }
  }
  return battlefieldsData;
}

export function getRandomBattlefield() {
  if (!battlefieldsData || battlefieldsData.length === 0) {
    return null;
  }

  // Filter to only active battlefields
  const activeBattlefields = battlefieldsData.filter(battlefield => battlefield.active);
  console.log("activeBattlefields: ", activeBattlefields);
  if (activeBattlefields.length === 0) {
    return null;
  }

  return activeBattlefields[Math.floor(Math.random() * activeBattlefields.length)];
}

// Get fresh battlefield data and load any new sprites
export async function getFreshRandomBattlefield(scene) {
  try {
    // Refresh battlefield data from server
    await refreshBattlefieldData();

    // Load any new sprite assets
    //await loadNewBattlefieldSprites(scene);

    // Return a random battlefield from the fresh data
    return getRandomBattlefield();
  } catch (error) {
    console.error('Failed to get fresh battlefield, using cached data:', error);
    return getRandomBattlefield();
  }
}

export function generateCampyMatchTitle(battlefield) {
  if (!battlefield) return "Epic Battle Royale";

  const { name, theme } = battlefield;

  // Title prefixes based on theme
  const prefixes = {
    dungeon: ['Sinister Showdown', 'Dark Duel', 'Cryptic Clash', 'Underground Upheaval', 'Nasty Clash'],
    forest: ['Woodland Warfare', 'Tree-top Tussle', 'Forest Frenzy', 'Grove Grind', 'Nasty Clash'],
    mountain: ['Peak Pandemonium', 'Summit Slaughter', 'Mountain Mayhem', 'Cliff-side Carnage', 'Nasty Clash'],
    desert: ['Sandstorm Slaughter', 'Desert Deathmatch', 'Dune Destruction', 'Oasis Obliteration', 'Nasty Clash'],
    castle: ['Castle Carnage', 'Fortress Fury', 'Ruins Rampage', 'Citadel Chaos', 'Nasty Clash'],
    swamp: ['Bog Battle', 'Swamp Slaughter', 'Marsh Massacre', 'Mire Mayhem', 'Nasty Clash'],
    volcano: ['Lava Lunacy', 'Volcano Vendetta', 'Magma Madness', 'Crater Carnage', 'Nasty Clash'],
    ice: ['Frozen Fury', 'Ice Age Annihilation', 'Tundra Terror', 'Glacier Grind', 'Nasty Clash']
  };

  // Title suffixes
  const suffixes = ['at', 'in', 'of', 'within', 'upon'];

  const themePrefixes = prefixes[theme] || ['Epic Battle'];
  const randomPrefix = themePrefixes[Math.floor(Math.random() * themePrefixes.length)];
  const randomSuffix = suffixes[Math.floor(Math.random() * suffixes.length)];

  return `${randomPrefix} ${randomSuffix} ${name}`;
}

// Intermission + items data caches
let intermissionsCache = null;
let itemsCache = null;
let musicManifestCache = null;
let sfxManifestCache = null;

export async function loadIntermissionsData() {
  if (!intermissionsCache) {
    try {
      await ensureThemeLoaded();
      intermissionsCache = getThemeData('intermissions.screens', []);
    } catch (e) {
      console.error('Failed to load intermissions:', e);
      intermissionsCache = [];
    }
  }
  return intermissionsCache;
}

export async function loadItemsData() {
  if (!itemsCache) {
    try {
      await ensureThemeLoaded();
      itemsCache = getThemeData('items', []);
      console.log('[Utils] itemsCache:', itemsCache);
    } catch (e) {
      console.error('Failed to load items:', e);
      itemsCache = [];
    }
  }
  return itemsCache;
}

export function buildItemsById(items) {
  const m = new Map();
  items.forEach(i => m.set(i.id, i));
  return m;
}

export async function loadMusicManifest() {
  if (!musicManifestCache) {
    try {
      await ensureThemeLoaded();
      const musicData = getThemeData('music.battle', []);

      // Normalize: allow legacy string entries or new {key,artist,title}
      const normalized = musicData.map(entry => {
        if (typeof entry === 'string') {
          const base = entry.split('/').pop() || entry;
          const name = base.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ');
          return { key: entry, artist: 'DaveY', title: name };
        }
        const key = entry.key || entry.path || '';
        const base = key.split('/').pop() || key;
        const inferred = (base || '').replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ');
        return {
          key,
          artist: entry.artist || 'DaveY',
          title: entry.title || inferred
        };
      });
      musicManifestCache = { battle: normalized };
    } catch (e) {
      console.error('Failed to load music manifest:', e);
      musicManifestCache = { battle: [] };
    }
  }
  return musicManifestCache;
}

export async function loadVictoryMusicManifest() {
  try {
    await ensureThemeLoaded();
    const musicData = getThemeData('music.victory', []);

    // Normalize: allow legacy string entries or new {key,artist,title}
    const normalized = musicData.map(entry => {
      if (typeof entry === 'string') {
        const base = entry.split('/').pop() || entry;
        const name = base.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ');
        return { key: entry, artist: 'DaveY', title: name };
      }
      const key = entry.key || entry.path || '';
      const base = key.split('/').pop() || key;
      const inferred = (base || '').replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ');
      return {
        key,
        artist: entry.artist || 'DaveY',
        title: entry.title || inferred
      };
    });
    return normalized;
  } catch (e) {
    console.error('Failed to load victory music manifest:', e);
    return [];
  }
}

export async function loadRewardsMusicManifest() {
  try {
    await ensureThemeLoaded();
    const musicData = getThemeData('music.rewards', []);

    // Normalize: allow legacy string entries or new {key,artist,title}
    const normalized = musicData.map(entry => {
      if (typeof entry === 'string') {
        const base = entry.split('/').pop() || entry;
        const name = base.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ');
        return { key: entry, artist: 'DaveY', title: name };
      }
      const key = entry.key || entry.path || '';
      const base = key.split('/').pop() || key;
      const inferred = (base || '').replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ');
      return {
        key,
        artist: entry.artist || 'DaveY',
        title: entry.title || inferred
      };
    });
    return normalized;
  } catch (e) {
    console.error('Failed to load rewards music manifest:', e);
    return [];
  }
}

export async function loadSfxManifest() {
  if (!sfxManifestCache) {
    try {
      await ensureThemeLoaded();
      const sfxData = getThemeData('sounds', {});
      sfxManifestCache = sfxData;
    } catch (e) {
      console.error('Failed to load sfx manifest:', e);
      sfxManifestCache = {};
    }
  }
  return sfxManifestCache;
}

export async function loadWeaponsManifest() {
  try {
    await ensureThemeLoaded();
    return getThemeData('weapons', {});
  } catch (e) {
    console.error('Failed to load weapons manifest:', e);
    return {};
  }
}

let foodDataCache = null;

export async function loadFoodData() {
  if (!foodDataCache) {
    try {
      await ensureThemeLoaded();
      foodDataCache = getThemeData('food', []);
    } catch (e) {
      console.error('Failed to load food data:', e);
      foodDataCache = [];
    }
  }
  return foodDataCache;
}

export async function loadSpellsSoundsManifest() {
  try {
    await ensureThemeLoaded();
    const spellSounds = getThemeData('spellSounds', []);
    // Convert array format to object format expected by SFX manager
    const manifest = {};
    if (spellSounds) {
      spellSounds.forEach(sound => {
        manifest[sound.key] = [sound.file];
      });
    }
    return manifest;
  } catch (e) {
    console.error('Failed to load spells sounds manifest:', e);
    return {};
  }
}

let shieldsDataCache = null;

export async function loadShieldsData() {
  if (!shieldsDataCache) {
    try {
      await ensureThemeLoaded();
      shieldsDataCache = getThemeData('shields', []);
    } catch (e) {
      console.error('Failed to load shields data:', e);
      shieldsDataCache = [];
    }
  }
  return shieldsDataCache;
}

export function getShieldForPlayer(player, topHolders = []) {
  if (!topHolders || topHolders.length === 0) return null;

  // Find the player's ranking in top holders
  const playerIndex = topHolders.findIndex(holder =>
    holder.owner === player.walletAddress
  );

  if (playerIndex === -1 || playerIndex >= 20) return null; // Only top 20 holders get shields

  // Get available shields
  const shields = shieldsDataCache || [];

  // Assign shield based on ranking (cycle through available shields)
  const shieldIndex = playerIndex % shields.length;
  return shields[shieldIndex];
}

// Heuristic with inventory weighting: healing > strength > mana > poison
export function computeWinChances(players, itemsById, center) {
  const invWeightByType = { healing: 1.0, strength: 0.7, mana: 0.4, poison: 0.3 };

  const scores = players.map(p => {
    const healthPct = Math.max(0, Math.min(1, p.health / p.maxHealth));
    const dist = Math.hypot(p.sprite.x - center.x, p.sprite.y - center.y);
    const distNorm = Math.min(1, dist / 1200);
    const centerControl = 1 - distNorm;

    let invScore = 0;
    (p.inventory || []).forEach(entry => {
      const it = itemsById.get(entry.itemId);
      if (!it) return;
      const base = invWeightByType[it.type] || 0;
      const tier = it.tier || 1;
      invScore += base * tier * (entry.qty || 1);
    });

    return { p, raw: 0.6 * healthPct + 0.2 * centerControl + 0.2 * invScore };
  });

  const minRaw = Math.min(...scores.map(s => s.raw));
  const shifted = scores.map(s => ({ p: s.p, raw: s.raw - Math.min(0, minRaw) + 0.001 }));
  const sum = shifted.reduce((a, b) => a + b.raw, 0) || 1;

  const result = new Map();
  shifted.forEach(s => {
    result.set(s.p.walletAddress || s.p.name, Math.round((s.raw / sum) * 100));
  });
  return result;
}

/**
 * Show a marquee message from anywhere in the application
 * @param {string} text - The message to display
 * @param {Object} options - Display options (color, duration, etc.)
 * @param {string} priority - Priority level: 'high', 'medium', or 'low'
 */
export function showMarqueeMessage(text, options = {}, priority = 'low') {
  MarqueeManager.addMessage(text, options, priority);
}

/**
 * Clear all marquee messages
 */
export function clearMarqueeMessages() {
  MarqueeManager.clear();
}

/**
 * Clear all marquee message queues
 */
export function clearMarqueeQueues() {
  MarqueeManager.clearQueues();
}

/**
 * Get a configuration value from the server
 * @param {string} key - Configuration key
 * @param {any} defaultValue - Default value if key doesn't exist
 * @returns {Promise<any>} Configuration value
 */
export async function getConfig(key, defaultValue = null) {
  try {
    const response = await fetch(`/api/config/${key}`, {
      headers: {
        'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
      }
    });

    if (!response.ok) {
      console.warn(`[getConfig] Failed to fetch config ${key}, using default`);
      return defaultValue;
    }

    const data = await response.json();
    if (data.success && data.value !== undefined) {
      return data.value;
    }

    return defaultValue;
  } catch (error) {
    console.error(`[getConfig] Error fetching config ${key}:`, error);
    return defaultValue;
  }
}